import { Alert, Badge, Button, Col, Row } from "reactstrap";
import { useMemo, useState } from "react";
import pluralize from "pluralize";

import useLeaveSharedRecordSync from "../../../api/api-hooks/sharedRecords/useLeaveSharedRecordSync";
import { SharedRecordsField } from "../../../types/GlobalInterfaces";
import { AppConfirm } from "../../../utils/PopupUtil";

interface Props {
  sharedRecord: SharedRecordsField;
  tenantID: string;
  recordID: string;
  onClose?: () => void;
}

const ShareTypeInfo = {
  Sync: {
    Description: "Sync Between All Clouds.",
    BadgeColor: "primary",
    TextColor: "text-success",
  },
  ReadOnly: {
    Description: "Sync Between All Clouds.",
    BadgeColor: "warning",
    TextColor: "text-warning",
  },
  Copy: {
    Description: "A Copy From The Original Record.",
    BadgeColor: "dark",
    TextColor: "text-dark",
  },
} as const;

export default function SyncDetails({
  sharedRecord,
  tenantID,
  recordID,
  onClose,
}: Props) {
  const { leaveSync, leavingSync } = useLeaveSharedRecordSync();
  const [error, setError] = useState("");

  async function handleLeaveSync() {
    if (!recordID || !sharedRecord.ModuleAPIName || !sharedRecord._id) return;

    const result = await AppConfirm({
      title: "Leave Sync?",
      text: "You won't receive further updates for this record. To resume updates, you must re-add the shared record using a shared link. Note that the link may no longer exist, requiring you to contact the original owner for a new one.",
      confirmButtonText: "Leave",
    });

    if (!result.isConfirmed) return;

    leaveSync({
      recordID: recordID,
      sharedRecordID: sharedRecord._id,
      moduleAPIName: sharedRecord.ModuleAPIName,
      onSuccess: () => {
        onClose && onClose();
      },
      onError: (err) => setError(err),
    });
  }

  const cloudsDetails = useMemo(() => {
    if (!sharedRecord?.CloudsList?.length) return [];

    return sharedRecord?.CloudsList.map((cloud) => {
      let syncType: "Sync" | "Copy" | "ReadOnly" = sharedRecord?.Sync
        ? "Sync"
        : "Copy";

      if (!!sharedRecord?.ReadOnly && !cloud?.Source) {
        syncType = "ReadOnly";
      }

      if (cloud.RemoteStatus === "Copy") {
        syncType = "Copy";
      }

      const info = ShareTypeInfo[syncType];
      const cloudOwner = cloud?.CloudID === sharedRecord?.CloudID;

      return {
        CloudName: cloud.CloudName ?? "",
        Owner: !!cloud?.Source,
        OwnerName: cloud?.CloudOwner ?? "",
        OwnerEmail: cloud?.CloudOwnerEmail ?? "",
        BadgeShareTextType: cloudOwner ? "Owner" : syncType,
        BadgeColor: cloudOwner ? "info" : info.BadgeColor,
        TextColor: cloudOwner ? "text-info" : info.TextColor,
        CloudID: cloud.CloudID,
      };
    });
  }, [sharedRecord?.CloudsList]);

  const isSync = useMemo(() => {
    return cloudsDetails.some(
      (cloud) =>
        cloud.CloudID === tenantID && cloud.BadgeShareTextType !== "Copy"
    );
  }, [cloudsDetails]);

  return (
    <div className="pt-3">
      <h4 className="text-dark mb-3">Clouds that are Sharing this Record</h4>
      <div>
        {cloudsDetails.map((cloud) => {
          return (
            <CloudDetail
              cloudName={cloud.CloudName}
              syncType={cloud.BadgeShareTextType}
              textColor={cloud.TextColor}
              badgeColor={cloud.BadgeColor}
              owner={cloud.OwnerName}
              ownerEmail={cloud.OwnerEmail}
            />
          );
        })}
        <hr className="mb-3" />
        <h4>Shared Record Stats</h4>
        This record has been added by{" "}
        {(sharedRecord?.CloudsList?.length ?? 1) - 1}{" "}
        {pluralize("cloud", (sharedRecord?.CloudsList?.length ?? 1) - 1)}
      </div>
      {error && (
        <Alert className="mt-4" color="danger">
          {error}
        </Alert>
      )}
      {tenantID !== sharedRecord.CloudID && isSync && (
        <Button
          className="tw:mt-4"
          onClick={handleLeaveSync}
          disabled={leavingSync}
          color="danger"
        >
          {leavingSync ? "Leaving Sync..." : "Leave Sync"}
        </Button>
      )}
    </div>
  );
}

function CloudDetail({
  cloudName,
  syncType,
  badgeColor,
  textColor,
  owner,
  ownerEmail,
}: {
  cloudName: string;
  syncType: string;
  badgeColor: string;
  textColor: string;
  owner: string;
  ownerEmail: string;
}) {
  return (
    <>
      <Row md={8} className="pb-4">
        <Col md={1}>
          <i
            className={`mdi mdi-cloud-sync-outline ${textColor}`}
            style={{ fontSize: 20 }}
          />
        </Col>
        <Col md={7}>
          <Row className="pt-1 pb-1">
            <strong
              className="text-dark pr-2 font-weight-500"
              style={{ fontSize: 15 }}
            >
              {cloudName}
            </strong>
            <Badge color={badgeColor} style={{ margin: 0 }}>
              {syncType}
            </Badge>
          </Row>
          <Row>
            <div className="d-flex" style={{ fontSize: 14 }}>
              <div className=" mr-1">Shared With:</div>
              <div className="font-weight-500">{owner}</div>
            </div>
          </Row>
          <Row>
            <div className="d-flex" style={{ fontSize: 14 }}>
              <div className="mr-1">Email:</div>
              <div className="font-weight-500">{ownerEmail}</div>
            </div>
          </Row>
        </Col>
      </Row>
    </>
  );
}
